import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ordersService,
  type ListOrdersParams,
  type OrderRecord,
  type PatchDueInstallmentPayload,
} from '@/services/orders.service';
import { QUERY_KEYS } from '@/utils/constants';

/** Sales → Orders: one row per purchase, newest first, searchable by client. */
export const useOrdersList = (params: ListOrdersParams = {}) =>
  useQuery({
    queryKey: [...QUERY_KEYS.ORDERS.LIST, params.search ?? '', params.from ?? '', params.to ?? ''],
    queryFn: () => ordersService.list(params),
    placeholderData: keepPreviousData,
  });

/**
 * One order by id. Fetched directly rather than looked up in the list, because
 * a just-created order is opened before the list has refetched - and because an
 * order reached by URL may not be on the current page of results at all.
 */
export const useOrder = (orderId: number | null) =>
  useQuery({
    queryKey: QUERY_KEYS.ORDERS.BY_ID(orderId as number),
    queryFn: () => ordersService.getById(orderId as number),
    enabled: orderId != null && Number.isFinite(orderId) && orderId > 0,
    staleTime: 0,
  });

/** Every purchase this client has made, oldest first. */
export const useLeadOrders = (leadId: number | null) =>
  useQuery({
    queryKey: QUERY_KEYS.ORDERS.FOR_LEAD(leadId as number),
    queryFn: () => ordersService.listForLead(leadId as number),
    enabled: leadId != null,
  });

/**
 * What the client currently holds, across all orders. Used by Step 2 to show an
 * "already subscribed" panel and to explain why a product is not selectable.
 */
export const useLeadSubscriptions = (leadId: number | null) =>
  useQuery({
    queryKey: QUERY_KEYS.ORDERS.SUBSCRIPTIONS(leadId as number),
    queryFn: () => ordersService.listSubscriptions(leadId as number),
    enabled: leadId != null,
  });

/**
 * Which higher tiers this client could move up to. Returns an empty target list
 * (not an error) when they have no eligible plan, so the caller can simply hide
 * the Upgrade action.
 */
export const useUpgradeOptions = (leadId: number | null, enabled = true) =>
  useQuery({
    queryKey: QUERY_KEYS.ORDERS.UPGRADE_OPTIONS(leadId as number),
    queryFn: () => ordersService.upgradeOptions(leadId as number),
    enabled: enabled && leadId != null,
  });

/**
 * Opens an Upgrade order. Same invalidations as the add-on path: the wizard
 * reopens bound to the new order, and the client's subscription list changes
 * shape once the upgrade completes.
 */
export const useCreateUpgradeOrder = () => {
  const queryClient = useQueryClient();
  return useMutation<OrderRecord, Error, { leadId: number; targetProductCode: string }>({
    mutationFn: ({ leadId, targetProductCode }) =>
      ordersService.createUpgrade(leadId, targetProductCode),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS.FOR_LEAD(order.leadId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORDERS.SUBSCRIPTIONS(order.leadId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORDERS.UPGRADE_OPTIONS(order.leadId),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SALES.CLIENTS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADS.ALL });
    },
  });
};

/**
 * Opens a new Add-on order for an existing client. Invalidates the lead and
 * client lists so the wizard reopens bound to the fresh, empty order rather than
 * the client's previous (completed) purchase.
 */
export const useCreateAddOnOrder = () => {
  const queryClient = useQueryClient();
  return useMutation<OrderRecord, Error, number>({
    mutationFn: (leadId: number) => ordersService.createAddOn(leadId),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS.FOR_LEAD(order.leadId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORDERS.SUBSCRIPTIONS(order.leadId),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SALES.CLIENTS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADS.ALL });
    },
  });
};

/**
 * Corrects one Due installment from the Orders queue - moving the promised date
 * or recording the payment that settles it (ADR-053).
 *
 * Invalidates the order lists rather than patching the cache: settling a Due
 * row changes the order's payment status and its outstanding total, which the
 * server derives.
 */
export const usePatchDueInstallment = () => {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { orderId: number; installmentNumber: number; payload: PatchDueInstallmentPayload }
  >({
    mutationFn: ({ orderId, installmentNumber, payload }) =>
      ordersService.patchDueInstallment(orderId, installmentNumber, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS.ROOT });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SALES.CLIENTS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADS.ALL });
    },
  });
};
